import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { ToggleGroup } from '@/components/Form/ToggleGroup';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { getDataTestId } from '@/utils/browser';
import { isNil } from '@/utils/common';
import { hasUSIndicia } from '@/utils/user';

import { TaxQuestionProps } from '../TaxInfoDrawer.interface';
import { TaxInfoQuestionForm } from '../TaxInfoQuestionForm';
import { useUsTaxReturnQuestionForm } from './useUsTaxReturnQuestionForm';
import { UsTaxReturnFollowUpSection } from './UsTaxReturnFollowUpSection';
import { getUsTaxNoReturnReasonGroupedSelectConfig } from './utils';

import {
  TaxInfoQuestionLabel,
  TaxInfoQuestions,
  TaxInfoQuestionSection,
} from '../TaxInfoDrawer.styled';

export function QuestionUSTaxReturn({
  index,
  total,
  onAnswered,
}: Readonly<TaxQuestionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const hasUsIndicia = hasUSIndicia(user);

  const {
    groups: noReturnReasonGroups,
    otherOption: noReturnReasonOtherOption,
  } = useMemo(() => getUsTaxNoReturnReasonGroupedSelectConfig(t), [t]);

  const {
    control,
    errors,
    canSubmit,
    isPending,
    error,
    onSubmit,
    handleTaxReturnChange,
    resetField,
    usTaxReturnRequired,
    taxNumberTypeCode,
  } = useUsTaxReturnQuestionForm({ hasUsIndicia, onAnswered });

  return (
    <TaxInfoQuestionForm
      index={index}
      total={total}
      sectionTitle={t('taxInfoDrawer.question1.sectionTitle')}
      answerError={error}
      isLoading={isPending}
      onSubmit={onSubmit}
    >
      <TaxInfoQuestionSection>
        <TaxInfoQuestions>
          <TaxInfoQuestionLabel variant="body1Medium">
            {t('taxInfoDrawer.question1.question')}
          </TaxInfoQuestionLabel>
          <Controller
            control={control}
            name="usTaxReturnRequired"
            render={({ field: { value, onChange } }) => (
              <ToggleGroup
                fullWidth
                size="medium"
                name="usTaxReturnRequired"
                options={[
                  { value: 'false', text: t('common.no') },
                  { value: 'true', text: t('common.yes') },
                ]}
                value={isNil(value) ? null : String(value)}
                onChange={(_event, selectedValue) => {
                  handleTaxReturnChange(selectedValue, onChange);
                }}
                {...getDataTestId('tax-info-us-return-toggle')}
              />
            )}
          />
          <UsTaxReturnFollowUpSection
            usTaxReturnRequired={usTaxReturnRequired}
            hasUsIndicia={hasUsIndicia}
            control={control}
            errors={errors}
            isPending={isPending}
            taxNumberTypeCode={taxNumberTypeCode}
            noReturnReasonGroups={noReturnReasonGroups}
            noReturnReasonOtherOption={noReturnReasonOtherOption}
            resetField={resetField}
          />
        </TaxInfoQuestions>
      </TaxInfoQuestionSection>

      <LoadingButton
        {...getDataTestId('tax-info-question-continue-button')}
        type="submit"
        variant="contained"
        fullWidth
        isLoading={isPending}
        disabled={!canSubmit || isPending}
      >
        {t('common.continue')}
      </LoadingButton>
    </TaxInfoQuestionForm>
  );
}
